import type { ReactNode } from "react";
import { Typography } from "@mui/material";
import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import { trimChars } from "@entrilia/shared/utilities/stringHelper";
import type { OrganizationInfo } from "../../../api/clientsManagementTypes";

export const doesClientCodeExist = (clientCode: string, organizations: OrganizationInfo[]): boolean => {
  if (!clientCode.trim()) {
    return false;
  }

  const normalizedCode = clientCode.toLowerCase().trim();

  return organizations.some(
    (org) =>
      org.clientCode.toLowerCase() === normalizedCode ||
      org.managesClients.some((client) => client.clientCode.toLowerCase() === normalizedCode)
  );
};

export const detectCompanyTypeFromName = (companyName: string): CompanyType => {
  // Use the same logic as BC company name processing - trim square brackets first
  const cleanedName = trimChars(companyName, "[", "]");

  // Remove remaining special characters (hyphens, underscores, etc.) and convert to lowercase
  // Replace any non-alphanumeric character with a space
  const nameLower = cleanedName.toLowerCase().replace(/[^a-z0-9\s]/g, " ");

  // Split by spaces to get individual words
  const words = nameLower.split(/\s+/).filter((word) => word.length > 0);

  if (words.includes("template")) {
    return "Template";
  }
  if (words.includes("training")) {
    return "Training";
  }
  if (words.includes("sandbox")) {
    return "Sandbox";
  }
  if (words.includes("demo")) {
    return "Demo";
  }

  return "Production";
};

export const getClientCodeHelperText = (
  validationError: string | undefined,
  clientCodeExists: boolean,
  continueIfExists: boolean
): ReactNode => {
  if (validationError) {
    return validationError;
  }

  if (!clientCodeExists) {
    return undefined;
  }

  if (continueIfExists) {
    return (
      <Typography component="span" variant="inherit">
        ⚠️ Client already exists and will be <strong>RECREATED</strong>
      </Typography>
    );
  }

  return "⚠️ Client already exists";
};
